import {Component, EventEmitter, Input, Output} from '@angular/core';
import {MatTableModule} from "@angular/material/table";
import {MatIconButton} from "@angular/material/button";
import {MatIcon} from "@angular/material/icon";
import {MatTooltip} from "@angular/material/tooltip";
import {HasPermissionDirective} from "../../../directives/permissions/has-permission";
import {PermissionTypes} from "../../../enum/PermissionTypes";
import {TableExportButtonsComponent} from "../../../components/table-export-buttons/table-export-buttons.component";
import {inject} from "@angular/core";
import {ITableExportRequest, TableExportService} from "../../../services/table-export.service";

export interface RoleTableColumn {
  key: string;
  header: string;
  getValue: (item: any) => string;
}

@Component({
  selector: 'app-role-table',
  standalone: true,
  imports: [
    TableExportButtonsComponent,
    MatTableModule,
    MatIconButton,
    MatIcon,
    MatTooltip,
    HasPermissionDirective
  ],
  templateUrl: './role-table.component.html',
  styleUrl: './role-table.component.scss'
})
export class RoleTableComponent {
  @Input() dataSource: any[] = [];
  @Input() columns: RoleTableColumn[] = [];
  @Input() removeTooltip = 'Remove';
  @Input() removeAriaLabel = 'Remove item';
  @Input() testIdPrefix = 'role';

  /**
   * Set this to offer CSV and PDF above the table (#1411: "anytime there is a table of information").
   * The columns already say what each cell contains, so the export is built from the same description the
   * table renders from and cannot drift from what is on screen. Left null the buttons do not appear, for the
   * tables where a row is a control rather than a record.
   */
  @Input() exportTitle: string | null = null;

  /** The scope of the rows — whose roles, which game. */
  @Input() exportSubtitle: string | null = null;

  @Output() remove = new EventEmitter<any>();

  protected readonly PermissionTypes = PermissionTypes;

  private tableExportService: TableExportService = inject(TableExportService);

  get displayedColumns(): string[] {
    return [...this.columns.map(c => c.key), 'actions'];
  }

  onRemoveClick(item: any) {
    this.remove.emit(item);
  }

  private buildExportRequest(): ITableExportRequest<any> {
    return {
      title: this.exportTitle ?? '',
      subtitle: this.exportSubtitle ?? undefined,
      summary: [{label: 'Rows', value: `${this.dataSource.length}`}],
      rows: this.dataSource,
      // The Actions column is left out: a remove button is not information, and a column of blanks in a
      // spreadsheet invites the question of what it was.
      columns: this.columns.map(columnP => ({header: columnP.header, value: columnP.getValue})),
      fileName: this.exportTitle
        ? this.exportTitle.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
        : 'table'
    };
  }

  onExportCsv(): void {
    this.tableExportService.exportCsv(this.buildExportRequest());
  }

  onExportPdf(): void {
    this.tableExportService.exportPdf(this.buildExportRequest());
  }
}
